import { useStudioColors } from '~/composables/useStudioColors'
import type { ColorChoice } from '~/composables/shapes/colors'
import { getShapeById, loadVariations, getColorVariation } from '~/composables/shapes/utils'
import { getShapeBaseConfig } from '~/composables/shapes/factory'
import { GRID_START_OFFSET } from '~/composables/grid/constants'
import type { ShapeInterface } from '~/types/grid.types'

// Mini studio data interfaces
export interface MiniStudioTile {
  x: number          // Position in pixels (100px increments)
  y: number
  rotation: number   // 0, 90, 180, 270 degrees
  pattern: string    // Pattern name like "Zen Garden"
  blockId: string    // Pattern ID like "tile_pictures_VJUxAX"
  tileType?: number  // NEW: Index of tile type for multi-tile patterns
}

// Pattern configuration for multi-tile patterns
export interface PatternConfig {
  type: 'multi-tile' | 'single-tile'
  tileCount: number
  distribution: string  // e.g., 'modulo'
  tileNames: string[]
}

// Tile metadata including product info and images
export interface TileMetadata {
  products: Array<{
    id: string
    title: string
    handle: string
  }>
  images: {
    light: string[]
    dark: string[]
  }
}

export interface MiniStudioDesign {
  tiles: MiniStudioTile[]
  tileTypes?: number[]         // NEW: Array of tile type indices
  width: number                // Grid width in tiles
  height: number               // Grid height in tiles
  gridSize: string             // Like "4x3"
  currentColor: string         // Hex color like "#f3f3f3"
  currentColorTitle: string    // Color name like "Zinc"
  patternChosen: string        // Same as blockId
  patternName: string          // Same as pattern
  patternConfig?: PatternConfig // NEW: Configuration for multi-tile patterns
  tileMetadata?: TileMetadata   // NEW: Metadata for tile types
}

/**
 * Parse the miniStudioDesign cookie
 */
export function parseMiniStudioCookie(): MiniStudioDesign | null {
  if (import.meta.server) return null
  
  try {
    const cookies = document.cookie.split(';')
    const miniStudioCookie = cookies.find(cookie => 
      cookie.trim().startsWith('miniStudioDesign=')
    )
    
    if (!miniStudioCookie) return null
    
    const cookieValue = miniStudioCookie.split('=')[1]
    const decodedValue = decodeURIComponent(cookieValue)
    const rawData = JSON.parse(decodedValue) as any

    // Check for enhanced multi-tile format with tile types
    const isEnhancedFormat = rawData && Array.isArray(rawData.r) && 
      (Array.isArray(rawData.tt) || rawData.pc || rawData.tm)
    
    // Support new enhanced format with multi-tile patterns
    if (isEnhancedFormat) {
      const width = Math.max(0, Number(rawData.w) || 0)
      const height = Math.max(0, Number(rawData.h) || 0)
      const rotations: number[] = rawData.r
      const tileTypes: number[] = rawData.tt || []
      const tiles: MiniStudioTile[] = []

      for (let row = 0; row < height; row += 1) {
        for (let col = 0; col < width; col += 1) {
          const idx = row * width + col
          const rotationCode = Number.isFinite(rotations[idx]) ? rotations[idx] : 0
          // Allow -1 to indicate empty cell (skip)
          if (rotationCode === -1) continue
          const normalizedQuarterTurns = ((rotationCode % 4) + 4) % 4
          
          // Handle tile types - if null or undefined, calculate based on distribution
          let tileType = tileTypes[idx]
          if (tileType === null || tileType === undefined) {
            // If we have a multi-tile pattern with modulo distribution, calculate the tile type
            if (rawData.pc?.type === 'multi-tile' && rawData.pc?.distribution === 'modulo') {
              const actualTileCount = rawData.pc.tileNames?.length || 3
              tileType = idx % actualTileCount
            } else {
              tileType = 0
            }
          }

          tiles.push({
            x: col * 100,
            y: row * 100,
            rotation: normalizedQuarterTurns * 90,
            pattern: String(rawData.pn || ''),
            blockId: String(rawData.p || ''),
            tileType: tileType
          })
        }
      }

      const enhancedDesign: MiniStudioDesign = {
        tiles,
        tileTypes: tileTypes.length > 0 ? tileTypes : undefined,
        width,
        height,
        gridSize: `${width}x${height}`,
        currentColor: String(rawData.c || ''),
        currentColorTitle: String(rawData.ct || ''),
        patternChosen: String(rawData.p || ''),
        patternName: String(rawData.pn || ''),
        // Add pattern configuration if present
        patternConfig: rawData.pc ? {
          type: rawData.pc.type || 'single-tile',
          tileCount: rawData.pc.tileCount || rawData.pc.tileNames?.length || 1,  // Use tileNames length if tileCount is 0
          distribution: rawData.pc.distribution || 'single',
          tileNames: rawData.pc.tileNames || []
        } : undefined,
        // Add tile metadata if present
        tileMetadata: rawData.tm ? {
          products: rawData.tm.products || [],
          images: {
            light: rawData.tm.images?.light || [],
            dark: rawData.tm.images?.dark || []
          }
        } : undefined
      }

      // Validate required fields
      if (!enhancedDesign.currentColorTitle || !enhancedDesign.patternName) {
        throw new Error('Missing color or pattern information (enhanced format)')
      }

      console.log('🎨 Mini Studio: Parsed enhanced format', {
        isMultiTile: enhancedDesign.patternConfig?.type === 'multi-tile',
        tileCount: enhancedDesign.patternConfig?.tileCount,
        tileNames: enhancedDesign.patternConfig?.tileNames
      })

      return enhancedDesign
    }

    // Support existing compact format for backward compatibility
    const isCompactFormat =
      rawData && Array.isArray(rawData.r) && typeof rawData.w === 'number' && typeof rawData.h === 'number'

    if (isCompactFormat) {
      const width = Math.max(0, Number(rawData.w) || 0)
      const height = Math.max(0, Number(rawData.h) || 0)
      const rotations: number[] = rawData.r
      const tiles: MiniStudioTile[] = []

      for (let row = 0; row < height; row += 1) {
        for (let col = 0; col < width; col += 1) {
          const idx = row * width + col
          const rotationCode = Number.isFinite(rotations[idx]) ? rotations[idx] : 0
          // Allow -1 to indicate empty cell (skip)
          if (rotationCode === -1) continue
          const normalizedQuarterTurns = ((rotationCode % 4) + 4) % 4

          tiles.push({
            x: col * 100,
            y: row * 100,
            rotation: normalizedQuarterTurns * 90,
            pattern: String(rawData.pn || ''),
            blockId: String(rawData.p || ''),
          })
        }
      }

      const compactDesign: MiniStudioDesign = {
        tiles,
        width,
        height,
        gridSize: `${width}x${height}`,
        currentColor: String(rawData.c || ''),
        currentColorTitle: String(rawData.ct || ''),
        patternChosen: String(rawData.p || ''),
        patternName: String(rawData.pn || ''),
      }

      // Validate required fields
      if (!compactDesign.currentColorTitle || !compactDesign.patternName) {
        throw new Error('Missing color or pattern information (compact format)')
      }

      return compactDesign
    }

    // Backward compatibility with legacy verbose format
    const legacyDesign = rawData as MiniStudioDesign

    if (!legacyDesign.tiles || !Array.isArray(legacyDesign.tiles)) {
      throw new Error('Invalid tiles data (legacy format)')
    }

    if (!legacyDesign.currentColorTitle || !legacyDesign.patternName) {
      throw new Error('Missing color or pattern information (legacy format)')
    }

    return legacyDesign
  } catch (error) {
    console.error('Failed to parse mini studio data:', error)
    return null
  }
}

/**
 * Check if a color is considered "dark" and should use dark tile images
 */
export function isDarkColor(colorTitle: string): boolean {
  const darkColors = ['ebony', 'admiral', 'cast', 'oxford']
  return darkColors.includes(colorTitle.toLowerCase())
}

/**
 * Map multi-tile pattern to V2 shapes
 * Each tile type in the pattern gets mapped to its corresponding shape
 */
export function mapMultiTilePattern(
  tileName: string, 
  tileTypeIndex: number,
  patternConfig?: PatternConfig
): string {
  // Multi-tile pattern mapping based on tile names
  const multiTilePatternMap: Record<string, string> = {
    // Zen Garden pattern tiles
    'Zen Tile': 'EngravedZen',
    'Large Parallel Tile': 'EngravedLargeParallel',
    'Tangent Tile': 'EngravedTangent',
    // Add more multi-tile patterns here as needed
  }
  
  console.log(`🔍 Mapping tile: "${tileName}" at index ${tileTypeIndex}`)
  
  // First try to map by tile name
  if (tileName && multiTilePatternMap[tileName]) {
    console.log(`✅ Found mapping: ${tileName} -> ${multiTilePatternMap[tileName]}`)
    return multiTilePatternMap[tileName]
  }
  
  // If no specific tile name, use the pattern name + index as fallback
  if (patternConfig && patternConfig.type === 'multi-tile') {
    const baseName = patternConfig.tileNames?.[tileTypeIndex]
    if (baseName && multiTilePatternMap[baseName]) {
      console.log(`✅ Found mapping via pattern config: ${baseName} -> ${multiTilePatternMap[baseName]}`)
      return multiTilePatternMap[baseName]
    }
  }
  
  // Default fallback to single tile mapping
  console.log(`⚠️ No mapping found for "${tileName}", using fallback Square`)
  return 'Square'
}

/**
 * Delete the miniStudioDesign cookie
 */
export function clearMiniStudioCookie(): void {
  if (import.meta.server) return
  
  // Set cookie to expire in the past to delete it
  // Try multiple approaches to ensure cookie deletion works in all environments
  
  // 1. Clear with .feltright.com domain (production)
  document.cookie = 'miniStudioDesign=; domain=.feltright.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
  
  // 2. Clear without domain (localhost and other environments)
  document.cookie = 'miniStudioDesign=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
  
  // 3. Clear with current hostname domain  
  const hostname = window.location.hostname
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    document.cookie = `miniStudioDesign=; domain=${hostname}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`
  }
  
  // Verify cookie was cleared (silent check)
  const cookies = document.cookie.split(';')
  const remainingCookie = cookies.find(cookie => 
    cookie.trim().startsWith('miniStudioDesign=')
  )
  
  if (remainingCookie) {
    console.warn('Mini studio cookie still exists after clearing attempt')
  }
}

/**
 * Map mini studio color title to V2 color choice
 */
export function mapMiniStudioColor(colorTitle: string): ColorChoice | null {
  // Handle special cases where mini studio has spaces but V2 doesn't
  const colorMapping: Record<string, string> = {
    'Baby Blue': 'BabyBlue',
    'Slate Blue': 'SlateBlue',
    // Add more mappings as needed
  }
  
  // Check if we have a direct mapping
  const mappedTitle = colorMapping[colorTitle] || colorTitle
  
  // Try exact match first with mapped title
  const { getColors } = useStudioColors()
  const colors = getColors()
  let colorChoice = colors.find(c => 
    c.label.toLowerCase() === mappedTitle.toLowerCase()
  )
  
  if (colorChoice) return colorChoice
  
  // Try removing spaces from both sides for comparison
  const normalizedTitle = colorTitle.replace(/\s+/g, '').toLowerCase()
  colorChoice = colors.find(c => 
    c.label.replace(/\s+/g, '').toLowerCase() === normalizedTitle
  )
  
  if (colorChoice) return colorChoice
  
  // Try partial match for common variations
  const simpleTitle = colorTitle.toLowerCase().trim()
  colorChoice = colors.find(c => 
    c.label.toLowerCase().includes(simpleTitle) ||
    simpleTitle.includes(c.label.toLowerCase())
  )
  
  if (colorChoice) return colorChoice
  
  // Fallback to first color if no match found
  console.warn(`Color "${colorTitle}" not found, using default color`)
  return colors[0] || null
}

/**
 * Map mini studio pattern to V2 shape
 * Maps specific patterns from mini studio to corresponding V2 engraved shapes
 */
export function mapMiniStudioPattern(patternName: string, _blockId: string) {
  // Pattern name mapping from mini studio to V2 shape IDs
  const patternMap: Record<string, string> = {
    'Zen Garden': 'EngravedZen',
    'Tangent': 'EngravedTangent', 
    'Large Parallel': 'EngravedLargeParallel',
    'Lg Parallel': 'EngravedLargeParallel',  // Support abbreviated form from mini studio
    'Knot': 'EngravedKnot',
    'Converge': 'EngravedConverge',
    'Subway': 'EngravedSubway',
    'Offset': 'EngravedOffset',
    'Topo': 'EngravedTopo',
    'River': 'EngravedRiver',
    'Cross': 'EngravedCross',
    'Four Square': 'EngravedFourSquare',
    'Parallel': 'EngravedParallel',
    'Back Slash': 'EngravedBackSlash',
    'Arc': 'EngravedArc'
  }
  
  // Try to find exact pattern match
  let shapeId = patternMap[patternName]
  
  // If no exact match, try fallback patterns
  if (!shapeId) {
    // Try partial matches for common variations
    const normalizedPattern = patternName.toLowerCase()
    if (normalizedPattern.includes('zen')) {
      shapeId = 'EngravedZen'
    } else if (normalizedPattern.includes('tangent')) {
      shapeId = 'EngravedTangent'
    } else if (normalizedPattern.includes('parallel')) {
      shapeId = 'EngravedParallel'
    } else if (normalizedPattern.includes('cross')) {
      shapeId = 'EngravedCross'
    } else {
      // Final fallback to basic square
      shapeId = 'Square'
    }
  }
  
  const shape = getShapeById(shapeId)
  
  
  if (!shape) {
    console.warn(`Shape "${shapeId}" not found for pattern "${patternName}", using fallback`)
    return getShapeById('Square') || getShapeById('LargeSquare')
  }
  
  return shape
}

/**
 * Helper function to snap a value to the nearest grid increment
 */
function snapToGrid(value: number, snapSize: number): number {
  return Math.round(value / snapSize) * snapSize
}

/**
 * Convert mini studio tiles to V2 shapes with proper image loading
 * Now supports multi-tile patterns where different tiles use different shapes
 */
export async function convertMiniStudioToV2Shapes(designData: MiniStudioDesign): Promise<ShapeInterface[]> {
  try {
    // Ensure variations are loaded before creating shapes
    await loadVariations()
    
    const colorChoice = mapMiniStudioColor(designData.currentColorTitle)
    if (!colorChoice) {
      throw new Error('Unable to map mini studio color')
    }
    
    const shapes: ShapeInterface[] = []
    const imageLoadPromises: Promise<void>[] = []
    const isMultiTile = designData.patternConfig?.type === 'multi-tile'
    const isDark = isDarkColor(designData.currentColorTitle)
    
    // Calculate positioning so tiles are touching each other directly
    // Each tile should be positioned adjacent to the next one based on actual tile size
    const RULER_SIZE = GRID_START_OFFSET  // 20px - space for rulers
    const MARGIN = 20  // Small margin from rulers
    
    // Square grid snap sizes from grid constants
    const SQUARE_SNAP_X = 30  // From GRID_PATTERN_LIST square grid snap.x
    const SQUARE_SNAP_Y = 30  // From GRID_PATTERN_LIST square grid snap.y
    
    for (let i = 0; i < designData.tiles.length; i++) {
      const tile = designData.tiles[i]
      const tileTypeIndex = tile.tileType ?? 0
      
      // Determine which shape to use based on tile type
      let shape
      if (isMultiTile && designData.patternConfig) {
        // For multi-tile patterns, get the tile name and map to shape
        const tileName = designData.patternConfig.tileNames?.[tileTypeIndex] || ''
        const shapeId = mapMultiTilePattern(tileName, tileTypeIndex, designData.patternConfig)
        shape = getShapeById(shapeId)
        
        if (!shape) {
          console.warn(`Shape "${shapeId}" not found for tile type ${tileTypeIndex}, using fallback`)
          shape = getShapeById('Square') || getShapeById('EngravedZen')
        }
      } else {
        // For single-tile patterns, use the pattern mapping
        shape = mapMiniStudioPattern(designData.patternName, designData.patternChosen)
      }
      
      if (!shape) {
        console.warn('Unable to determine shape for tile:', tile)
        continue
      }
      
      // Create base shape configuration
      const baseShape = getShapeBaseConfig(shape, colorChoice, true) as any
      
      if (!baseShape) {
        console.warn('Failed to create base shape for tile:', tile)
        continue
      }
      
      // SPECIAL CASE: Mini studio rotation system differs from main app for certain patterns
      // For Topo pattern: mini studio r[0] = -90° in main app (or +270°)
      let adjustedRotation = tile.rotation
      if (shape.id === 'EngravedTopo') {
        adjustedRotation = (tile.rotation - 90 + 360) % 360 // Convert mini studio rotation to main app rotation
      }
      
      // Apply the adjusted rotation to the base shape
      baseShape.rotation = adjustedRotation
      
      // No need to load custom images from Shopify - the app's shape system already has the correct images
      // The getShapeBaseConfig function already loads the proper image based on the shape and color
      
      // Create image loading promise if shape has an image
      if (baseShape.fillPatternImage) {
        const imagePromise = new Promise<void>((resolve, reject) => {
          if (baseShape.fillPatternImage.complete) {
            // Image already loaded
            resolve()
          } else {
            // Wait for image to load
            baseShape.fillPatternImage.onload = () => resolve()
            baseShape.fillPatternImage.onerror = (error: any) => {
              console.error('Mini studio image failed to load:', error)
              reject(error)
            }
          }
        })
        imageLoadPromises.push(imagePromise)
      }
      
      // Note: Tile dimensions available in baseShape.width/height if needed
      
      // Convert mini studio coordinates directly to touching tile positions
      // Mini studio: 100px increments represent tile positions (0, 1, 2, etc.)
      // V2: Position tiles so they touch each other
      
      const tileX = Math.floor(tile.x / 100)  // Convert to tile grid position
      const tileY = Math.floor(tile.y / 100)  // Convert to tile grid position
      
      // Calculate position where tiles touch each other:
      // For proper grid snapping, we need to align to multiples of the snap size (30px)
      // Since tiles are 240px (8 * 30px), they naturally align if we position them correctly
      
      // Calculate the snapped position for each tile
      // Tiles need to be positioned at valid snap points: startOffset + n * snapSize
      // For square grid: valid positions are 20 + n*30 (e.g., 20, 50, 80, 110, 140, 170, 200, 230, 260, 290...)
      
      // Since tiles are 240px wide/tall, we want their centers at positions that will snap correctly
      // A 240px tile has its center 120px from its edge
      // To make the center snappable, we need: center = 20 + n*30
      
      // For a 4x3 grid of tiles, we want spacing of 240px (8 snap units)
      // First tile center should be at a snappable position
      // Let's put first tile center at 140 (20 + 4*30), which gives us room for the tile
      // This means tile edge starts at 140-120=20 (perfect alignment with ruler edge)
      
      // Calculate position for each tile maintaining 240px spacing
      // First tile center: 140 (20 + 120)
      // Second tile center: 380 (140 + 240) = 20 + 360 = 20 + 12*30 ✓
      // Third tile center: 620 (380 + 240) = 20 + 600 = 20 + 20*30 ✓
      // Fourth tile center: 860 (620 + 240) = 20 + 840 = 20 + 28*30 ✓
      
      const FIRST_TILE_CENTER_X = RULER_SIZE + 120  // 140 = 20 + 120 (4 snap units from ruler)
      const FIRST_TILE_CENTER_Y = RULER_SIZE + 120  // 140 = 20 + 120 (4 snap units from ruler)
      const TILE_SPACING = 240  // 8 snap units of 30px each
      
      // Calculate snapped position for this tile
      const v2X = FIRST_TILE_CENTER_X + (tileX * TILE_SPACING)
      const v2Y = FIRST_TILE_CENTER_Y + (tileY * TILE_SPACING)
      
      
      const v2Shape: ShapeInterface = {
        ...baseShape,
        id: `mini-studio-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        x: v2X,
        y: v2Y,
        rotation: adjustedRotation,
        // Ensure proper rotation center (offset values)
        offsetX: baseShape.offsetX || (baseShape.width ? baseShape.width / 2 : 120),
        offsetY: baseShape.offsetY || (baseShape.height ? baseShape.height / 2 : 120),
        // Ensure the shape has proper attributes
        attrs: {
          ...baseShape.attrs,
          x: v2X,
          y: v2Y,
          rotation: adjustedRotation,
          offsetX: baseShape.offsetX || (baseShape.width ? baseShape.width / 2 : 120),
          offsetY: baseShape.offsetY || (baseShape.height ? baseShape.height / 2 : 120),
        }
      }
      
      // Add product metadata if available for multi-tile patterns
      if (isMultiTile && designData.tileMetadata?.products?.[tileTypeIndex]) {
        const productInfo = designData.tileMetadata.products[tileTypeIndex]
        // Only update product_id if we have complete metadata structure
        if (v2Shape.metadata && typeof v2Shape.metadata === 'object') {
          v2Shape.metadata.product_id = productInfo.id
          // Note: Additional product info (title/handle) not stored in metadata
          // as it's not part of the ShapeMetadata interface
        }
      }
      
      shapes.push(v2Shape)
    }
    
    // Wait for all images to load before returning shapes
    if (imageLoadPromises.length > 0) {
      await Promise.all(imageLoadPromises)
    }
    
    return shapes
  } catch (error) {
    console.error('Failed to convert mini studio data:', error)
    throw new Error('Failed to convert mini studio design data')
  }
}

/**
 * Calculate appropriate grid size for mini studio design
 */
export function calculateGridSize(designData: MiniStudioDesign): { width: number, height: number } {
  // Import the constant from the app's grid constants
  const FOOT_IN_PIXELS = 120  // Matches the app's constant: 120px per foot
  
  // Mini studio sends dimensions in 2-foot increments
  // width: 2 = 4 feet, height: 1 = 2 feet  
  // Each unit represents 2 feet
  const FEET_PER_UNIT = 2
  
  // Calculate grid size based on mini studio dimensions
  // Convert mini studio units to feet, then to pixels
  const gridWidth = designData.width * FEET_PER_UNIT * FOOT_IN_PIXELS
  const gridHeight = designData.height * FEET_PER_UNIT * FOOT_IN_PIXELS
  
  
  return {
    width: gridWidth,
    height: gridHeight
  }
}

/**
 * Main function to load mini studio design with image preloading
 */
export async function loadMiniStudioDesign(): Promise<{
  shapes: ShapeInterface[]
  gridSize: { width: number, height: number }
  colorChoice: ColorChoice
} | null> {
  try {
    const designData = parseMiniStudioCookie()
    if (!designData) return null
    
    
    // Wait for all images to load
    const shapes = await convertMiniStudioToV2Shapes(designData)
    const gridSize = calculateGridSize(designData)
    const colorChoice = mapMiniStudioColor(designData.currentColorTitle)
    
    if (!colorChoice) {
      throw new Error('Unable to map color choice')
    }
    
    
    return {
      shapes,
      gridSize,
      colorChoice
    }
  } catch (error) {
    console.error('Failed to load mini studio design:', error)
    return null
  }
}